import React, { createElement, useContext, useEffect, useState } from "react";
import { ForwardTransform, ReverseTransform } from "./transformation/Transformation";
import { PlotAffine, Affine } from "../types/PlotAffineType";
import { useGeoApps } from "@mapgear/geoapps-ui-framework";
import { FeatureInfoContext } from "./FeatureInfoContext";
import Static from "ol/source/ImageStatic.js";
import * as olProj from 'ol/proj';
import { transform } from 'ol/proj';
import { LayerItemId } from "../types/LayerItemId";

type LegendLayerProps = {
    /**
     * The layer
     */
    layer: any;

    /**
     * Name of the layer
     */
    layerName: string

    /**
     * Type of layer 
     */
    layerType: "plots" | "help" | "cadastral" | "other";
}

export const LegendLayer = (props: LegendLayerProps): JSX.Element => {
    const { geoapps } = useGeoApps();
    const [enabled, setEnabled] = useState<boolean>(props.layer ? props.layer.IsEnabled : false);
    const [init, setInit] = useState<boolean>(false);
    const [hpLayers, setHpLayers] = useState<any>();
    const { featureInfo } = useContext(FeatureInfoContext);
    const map = geoapps.GetMap("map");

    // Init help layers
    useEffect(() => {
        if (!map) return;

        // Disable other layers on app render
        disableOtherLayers();

        if (!hpLayers) {
            const helpLayer = map.Layers.Items.filter(layer => layer.LayerItemId === LayerItemId.HELP);
            if (helpLayer.length === 0) return;

            // Enable the parent help layer
            helpLayer[0].Enabled = true;

            // Disable all help layers
            disableAllHelpLayers(helpLayer[0]);

            setHpLayers(helpLayer[0]);
        }
    }, [map]);

    // Toggle layer
    useEffect(() => {
        if (!init) {
            setInit(true);
            return;
        }

        // Change visibility of the layer
        if (props.layerType !== "plots" && props.layerType !== "other") {
            props.layer.setVisible(enabled);
        } else {
            props.layer.Enabled = enabled;
        }

        // Toggle help layer
        if (props.layerType === "help") {
            if (!enabled) {
                disableAllHelpLayers(hpLayers);
            } else {
                enableHelpLayer();
            }
        }
    }, [enabled]);

    // Toggle correct layer
    useEffect(() => {
        toggleLayer();

    }, [featureInfo.nr, featureInfo.toggle, featureInfo.kt]);

    /**
     * Toggle the corresponding layer
     * @param mapKt Id of the map
     */
    const toggleLayer = () => {
        const mapKt = featureInfo.kt;
        if (!mapKt) return;
        switch (mapKt) {
            case "k1832":
            case "1832":
                if (props.layerName === "kaart 1832") {
                    setEnabled(true);
                } else if (props.layerType !== "plots") {
                    setEnabled(false);
                }
                break;
            case "1862":
            case "k1862":
            case "1860":
                if (props.layerName === "kaart 1860") {
                    setEnabled(true);
                }
                else if (props.layerType !== "plots") {
                    setEnabled(false);
                }
                break;
            case "1888":
            case "k1888":
            case "1890":
                if (props.layerName === "kaart 1888") {
                    setEnabled(true);
                }
                else if (props.layerType !== "plots") {
                    setEnabled(false);
                }
                break;
            case "k1966":
            case "1966":
                if (props.layerName === "kaart 1966") {
                    setEnabled(true);
                }
                else if (props.layerType !== "plots") {
                    setEnabled(false);
                }
                break;
            case "BRK":
            case "brk":
                if (props.layerName === "kaart BRK") {
                    setEnabled(true);
                }
                else if (props.layerType !== "plots") {
                    setEnabled(false);
                }
                break;
            default:
                if (props.layerType !== "help") return;
                getHelpMap(mapKt);
                break;
        }
    };

    /**
     * Retrieves the help map
     * @param kt Year or id of the help map
     * @param toggle Prop on whether to add or remove a layer
     * */
    const getHelpMap = async (kt: string | undefined) => {
        try {
            if (!kt) return;
            // Remove existing source
            props.layer.setSource(null);
            
            // Get the image data
            const result = await fetch(`~/resources/json/${kt}.json`);
            if (!result.ok) {
                // Check if the help layer is available within geoapps
                if (!hpLayers) return;

                // Disable all help layers if needed
                disableAllHelpLayers(hpLayers);

                // Enable the correct help layer
                enableHelpLayer();

                setEnabled(true);
                return;
            }
            // When the layer is not available within GeoApps, check if the layer is available in the json and img folder
            const data: PlotAffine = await result.json();
            if (!data?.kaart?.image?.url) return;

            // Retrieve image and coordinate settings
            const width = data.kaart.image.width;
            const height = data.kaart.image.height
            const anchor = calcAnchor(data.kaart.image.affine, height, width);
            const imageExtent = [0, 0, width, height];

            // Build url of image for source
            const url = `~/resources/jpg/${data.kaart.image.url}`;

            // Build projection
            const hkProjection = buildProjection(imageExtent, anchor);

            // Create source
            const hkSource = new Static({
                url: url,
                imageSize: [width, height],
                projection: hkProjection,
                imageExtent: imageExtent,
            });

            // Set source and toggle layer
            props.layer.setSource(hkSource);
            setEnabled(true);
        }
        catch (error) {
            console.error("Error: ", error);
        }
    };

    /**
     * Disable all help layers
     * @param hpLayers Help layers
     */
    const disableAllHelpLayers = (hpLayers) => {
        if (!hpLayers) return;
        hpLayers.ChildLayers.forEach((layer: any) => {
            layer.Enabled = false;
        });
    };

    /**
     * Enable the correct help layer
     */
    const enableHelpLayer = () => {
        if (!hpLayers || !featureInfo.kt) return;
        hpLayers.childLayers.filter((layer: any) => {
            if (layer.Title === featureInfo.kt) {
                layer.Enabled = true;
            }
        });
    };

    /**
     * Disable all 'other' layers
     */
    const disableOtherLayers = () => {
        if (props.layerType === "other") {
            setEnabled(false);
        }
    }

    /** 
     * Build the projection for the help layers
     * @param imageExent Extent of the image (width and height of the image)
     * @param anchorPoints Anchor points of the image
     * @returns Projection
     * */
    const buildProjection = (imageExent: number[], anchorPoints: number[][]) => {
        // Create projection
        const hkProjection = new olProj.Projection({
            code: "HULPKAART",
            extent: imageExent,
        });

        // Get map object and projection    
        const mapObj = map.MapEngine.Map.GetInternalMapObject();
        const mapProjection = mapObj.getView().getProjection();

        // transform anchor points to map projection
        const newAnchor = anchorPoints.map(coord => {
            return transform(coord, "EPSG:28992", mapProjection);
        });

        // Add coordinate transforms
        olProj.addCoordinateTransforms(mapProjection, hkProjection,
            function (coordinate: number[]) {
                const forwardResult = ForwardTransform(imageExent, newAnchor, coordinate);
                return forwardResult;
            },
            function (coordinate: number[]) {
                const reverseResult = ReverseTransform(imageExent, newAnchor, coordinate);
                return reverseResult;
            });

        return hkProjection;
    };

    /**
    * Calculates a corner point of the image
    * @param affine Image properties
    * @param width Width of the image
    * @param height Height of the image
    * @returns Anchor point of image
    */
    const formula = (affine: Affine, width: number, height: number): number[] => {
        return [affine.A * width + affine.B * height + affine.C, affine.D * width + affine.E * height + affine.F];
    };

    /**
     * computes the anchor points of the image in map coordinates
     * @param affine affine object
     * @param height image height in px
     * @param width image width in px
     * @returns anchor points
     */
    const calcAnchor = (affine: Affine, height: number, width: number) => {
        // Compute anchor points
        const topLeft = formula(affine, 0, 0);
        const topRight = formula(affine, width, 0);
        const bottomRight = formula(affine, width, height);
        const bottomLeft = formula(affine, 0, height);

        const anchor = [bottomLeft, topLeft, topRight, bottomRight];

        return anchor
    };

    /**
     * Creates hyperlink for layer
     * @param name name of the layer
     * @returns hyperlink or undefined
     */
    const createHyperlink = (name: string) => {
        if (name.startsWith("Markten")) {
            return createElement("a", {
                href: "https://www.archieven.nl/nl/zoeken?mivast=0&mizig=210&miadt=141&miaet=1&micode=0157.1&minr=15639531&miview=inv2",
                target: "_blank",
                rel: "noopener noreferrer",
            }, "HCO"
            )
        } else if (name.startsWith("Kaart der Veenen")) {
            return createElement("a", {
                href: "https://www.archieven.nl/nl/zoeken?mivast=0&mizig=210&miadt=141&miaet=1&micode=0157.1&minr=15639531&miview=inv2",
                target: "_blank",
                rel: "noopener noreferrer",
            }, "HCO"
            )
        } else {
            return undefined;
        }
    };

    /**
     * Create title text for layer
     * @param name name of the layer
     * @returns title text
     */
    const createTitle = (name: string) => {
        if (name.startsWith("Markten")) {
            return "Markten kaart met gemeten veendikte, 1811."
        } else if (name.startsWith("Kaart der Veenen")) {
            return "Kaart der Veenen, gelegen in de markten van Oosterhuis, Arrien, Stegeren, Reese, Diffelen, Heemse, Lutten en Collendoorn. ca 1820."
        }
        else if (name.startsWith("Gemeente Avereest")) {
            return "Gemeente Avereest 1856 uit de Gemeente-Atlas van Nederland van J. Kuyper, uitgegeven in 1871 door Hugo Suringar te Leeuwarden."
        }
        else {
            return name;
        }
    };

    return (
        <label>
            <div>
                <input
                    type="checkbox"
                    onClick={() => setEnabled(!enabled)}
                    checked={enabled}
                    title={createTitle(props.layerName)}
                />
                <span title={createTitle(props.layerName)}>{props.layerName} {createHyperlink(props.layerName)}</span>
            </div>
        </label>
    );
};

